import { ChangeDetectorRef, Component, HostListener, Injector, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Document as DocumentReact, FileType as FileTypeReact } from '@office-start/document-types';
import { getFileClickData, getReferralFromFileType } from '@office-start/file-utility';
import { WdOriginType as WdOriginTypeInst } from '@office-start/instrumentation-types';
import { logError, logFeature, logUserAction } from '@office-start/telemetry-service';
import { DataSecurityLevel as otelDataSecurityLevel, KnownErrorInstrumentationEvents, KnownFeatureNames } from '@office-start/telemetry-types';
import { getReferral } from '@office-start/telemetry-utility';
import { ThemeType } from '@suiteux/suiteux-shell';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subscription } from 'rxjs';
import { combineLatest, filter, map, take } from 'rxjs/operators';

import { AnnouncementType } from '../banner/announcement-type.enum';
import { AnnouncementModel } from '../banner/announcement.model';
import { BannerConfigService } from '../banner/banner-config.service';
import { FeatureCalloutService } from '../feature-callout/feature-callout.service';
import { FirstRunCalloutsConfigService } from '../first-run/first-run-callouts/first-run-callouts-config.service';
import { FirstRunCalloutsService } from '../first-run/first-run-callouts/first-run-callouts.service';
import { FirstRunConfigService } from '../first-run/first-run-component/first-run-config.service';
import { FirstRunDataService } from '../first-run/shared/first-run-data.service';
import { InstallConfigService } from '../install/install-config.service';
import { SearchConfigService } from '../search-results/services/search-config.service';
import { LinkTarget } from '../shared/constants/link-target.enum';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { RoutePaths } from '../shared/constants/route-paths.constant';
import { ThemeRootCssClass } from '../shared/constants/theme-shade.enum';
import { ClassNames } from '../shared/icon-font/icon-font-classes.utility';
import { IconFontType } from '../shared/icon-font/icon-font-type.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents, KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { FileType } from '../shared/models/file-item.model';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { WdOriginType } from '../shared/models/telemetry-constants.model';
import { DocumentInfo } from '../shared/models/wac-host.model';
import { MruFileItem } from '../shared/mru/mru-file-item.model';
import { AccessTokenService } from '../shared/services/access-token.service';
import { AdminPolicyService } from '../shared/services/admin-policy.service';
import { CreateDocumentResult, CreateDocumentResultType, CreateDocumentService } from '../shared/services/create-document.service';
import { DocumentService } from '../shared/services/document.service';
import { EventService } from '../shared/services/event.service';
import { FeedbackConfigService } from '../shared/services/feedback-config.service';
import { FeedbackService } from '../shared/services/feedback.service';
import { FloodgateService } from '../shared/services/floodgate.service';
import { JumpListItem, JumpListService } from '../shared/services/jump-list.service';
import { LocalAppLauncherService } from '../shared/services/local-app-launcher.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { PinToTaskbarService } from '../shared/services/pin-to-taskbar.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { ShellShimService } from '../shared/services/shell-shim.service';
import { ThemingService } from '../shared/services/theming.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { VisualService } from '../shared/services/visual.service';
import { WacContentService } from '../shared/services/wac-content.service';
import { WindowService } from '../shared/services/window.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { ReactShellConfigService } from '../shared/shared-context/react-shell-config.service';
import { UserContextService } from '../shared/shared-context/user-context.service';
import { WorkloadPublicDataConfigService } from '../shared/shared-context/workload-publicdata-config.service';
import { DragAndDrop } from '../shared/utilities/drag-and-drop.utility';
import { UploadButtonService } from '../upload/upload-button/upload-button.service';
import { UploadConfigService } from '../upload/upload-config.service';
import { UploadActionType, UploadLocalFileService } from '../upload/upload-local-file/upload-local-file.service';
import { UpsellDataService } from '../upsell/upsell-data.service';
import { PwaConfigService } from './services/pwa-config.service';

import './pwa-root.component.css';

// tslint:disable:naming-convention
declare namespace HomePage {
    abstract class ShellUtil {
        public static ShowOCVFeedbackLink(shellConfig?: any): void;
    }
}

// TODO: Refactor to share code between pwa, edge, and officehome
@Component({
    selector: 'ohp-pwa-root',
    templateUrl: './pwa-root.component.html'
})

export class PwaRootComponent implements OnDestroy, OnInit {
    public actionContextMenuEnabled: boolean;
    public appBarEnabled: boolean;
    public appsModuleEnabled: boolean;
    public backgroundStyle: { [key: string]: string } = {};
    public createOneNotePageEnabled: boolean;
    public customPivots: boolean;
    public firstRunEnabled: boolean;
    public fluentHeaderEnabled: boolean;
    public fluentThemeRefreshEnabled: boolean;
    public hasLoadedWacDoc: Observable<boolean>;
    public isConsumer: boolean;
    public leftNavReact: boolean;
    public loadFeatureCalloutModule: boolean;
    public loadTeachingUiReactModule: boolean;
    public teachingUiReactModuleEnabled: boolean;
    public memoryProfilingEnabled: boolean;
    public nativeSearchEnabled: boolean;
    public officeHomeClass: Array<string> = [];
    public onAppGallery: boolean;
    public onMyContentPage: boolean;
    public onFeedPage: boolean;
    public onWacContentPage: Observable<boolean>;
    public searchEnabled: boolean;
    public showBackgroundImage: boolean;
    public showFASTBreadcrumb: boolean;
    public showWorkspaceNav: boolean;
    public taskModule: boolean;
    public themeRootCssClass: string;
    public toastContainer: boolean;
    public uploadV2: boolean;
    public visualBackgroundEnabled: boolean;
    public visualOfficeWrapperStyle: { [key: string]: string } = {};
    public wacHostEnabled: boolean;
    public webVitalsEnabled: boolean;
    public viewInitializationCompleted = false;
    public showAnnouncement: boolean = false;

    private static readonly PwaOnActivatePendingEventArgsKey = 'PwaOnActivatePendingEventArgs';
    private readonly firstVisitTimestampKey = '_PwaFirstVisitTimestamp';

    private backgroundImageSubscription: Subscription;
    private featureCalloutSubscription: Subscription;
    private contentHeight: number;
    private contentWidth: number;
    private contextService: ContextService;
    private officeRouteService: OfficeRouteService;
    private shellHeader: HTMLElement;
    private themingService: ThemingService;
    private dragAndDropUtility: DragAndDrop;
    private firstRunVersion: number;
    private isPwaJumpListEnabled: boolean;
    private upsellButtonUtility: import ('../shared/utilities/upsell-button.utility').UpsellButton;
    private meetNowButtonUtility: import ('../shared/utilities/meet-now-button.utility').MeetNowButton;
    private shellBootstrapService: import('../shared/services/shell-bootstrap.service').ShellBootstrapService;
    private routeChangeSubscription: Subscription;
    private interactiveAppDisplayed: Promise<void>;
    private fluentProviderClassSubscription: Subscription;
    private visualConfigSubscription: Subscription;
    private visualService: VisualService;

    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly bannerConfigService: BannerConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly cookieService: CookieService,
        private readonly createDocumentService: CreateDocumentService,
        private readonly documentService: DocumentService,
        private readonly eventService: EventService,
        private readonly featureCalloutService: FeatureCalloutService,
        private readonly firstRunCalloutsConfigService: FirstRunCalloutsConfigService,
        private readonly firstRunCalloutsService: FirstRunCalloutsService,
        private readonly firstRunDataService: FirstRunDataService,
        private readonly floodgateService: FloodgateService,
        private readonly injector: Injector,
        private readonly instrumentationService: InstrumentationService,
        private readonly jumpListService: JumpListService,
        private readonly localAppLauncherService: LocalAppLauncherService,
        private readonly localStorageService: LocalStorageService,
        private readonly pinToTaskbarService: PinToTaskbarService,
        private readonly router: Router,
        private readonly windowService: WindowService,
        private readonly uploadLocalFileService: UploadLocalFileService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly wacContentService: WacContentService
    ) {}

    public ngOnInit() {
        this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.appInitialized),
            take(1))
            .toPromise()
            .then(async () => {
                const startupConfigService = this.injector.get(ClientStartupConfigService);
                const pwaConfigService = this.injector.get(PwaConfigService);

                this.interactiveAppDisplayed = Promise.resolve();
                this.isPwaJumpListEnabled = pwaConfigService.config.pwaJumpListEnabled;
                this.themeRootCssClass = ThemeRootCssClass;
                this.contextService = this.injector.get(ContextService);
                this.isConsumer = startupConfigService.config.authType === AuthType.MSA;
                this.officeRouteService = this.injector.get(OfficeRouteService);
                const consumerInstallConfigService = this.injector.get(ConsumerInstallConfigService);
                const consumerPublicDataService = this.injector.get(WorkloadPublicDataConfigService);
                const installConfigService = this.injector.get(InstallConfigService);
                const upsellDataService = this.injector.get(UpsellDataService);
                const urlLauncherService = this.injector.get(UrlLauncherService);
                const locService = this.injector.get(LocService);
                const shellShimService = this.injector.get(ShellShimService);
                const reactShellConfigService = this.injector.get(ReactShellConfigService);
                this.themingService = this.injector.get(ThemingService);
                this.visualService = this.injector.get(VisualService);
                this.leftNavReact = this.capabilitiesService.capabilities.leftNavReact && !this.capabilitiesService.capabilities.appBarEnabled;
                const newLeftNav = this.capabilitiesService.capabilities.leftNavReact || this.capabilitiesService.capabilities.appBarEnabled;
                this.showAnnouncement = this.capabilitiesService.capabilities.announcementEnabled;
                this.appBarEnabled = this.capabilitiesService.capabilities.appBarEnabled;
                this.appsModuleEnabled = this.capabilitiesService.capabilities.appsModuleEnabled;
                this.wacHostEnabled = this.capabilitiesService.capabilities.wacHostEnabled;
                this.taskModule = this.capabilitiesService.capabilities.m365AppsEnabled;
                this.webVitalsEnabled = this.capabilitiesService.capabilities.webVitalsEnabled;
                this.memoryProfilingEnabled = this.capabilitiesService.capabilities.memoryProfilingEnabled;
                this.fluentThemeRefreshEnabled = this.capabilitiesService.capabilities.fluentRefresh;
                this.fluentHeaderEnabled = this.capabilitiesService.capabilities.fluentHeader;
                this.toastContainer = this.capabilitiesService.capabilities.contentActionsEnabled;
                this.uploadV2 = this.capabilitiesService.capabilities.uploadV2Enabled;
                this.createOneNotePageEnabled = this.capabilitiesService.capabilities.createOneNotePageEnabled;
                this.customPivots = newLeftNav && this.capabilitiesService.capabilities.quickAccessV1CustomPivotEnabled;
                this.actionContextMenuEnabled = this.capabilitiesService.capabilities.contentActionsEnabled && this.capabilitiesService.capabilities.contentActionsOHomeEnabled;
                const defaultHeaderLayoutEnabled = this.capabilitiesService.capabilities.defaultHeaderLayoutEnabled;

                // Set background image
                this.backgroundImageSubscription = this.themingService.themeShades.pipe(
                    combineLatest(this.officeRouteService.getRouteChangeStartObservable(), (themeEvent, routeEvent) => ({ themeEvent, routeEvent }))
                ).subscribe(({ themeEvent, routeEvent }) => {
                    // Only show image on fluent commercial/consumer pages without app theming (Homepage, App Gallery, and Search from either)
                    // Also only on the base theme and when tenant theming isn't enabled
                    const route = routeEvent.currentRoute;
                    this.showBackgroundImage =
                            !themeEvent.theme.reactShellTheme.IsDarkMode
                            && ((themeEvent.theme.themeType === 'App' as ThemeType || themeEvent.theme.themeType === 'Default' as ThemeType) || this.isConsumer)
                            && !!this.contextService.sharedContext.fluentHeroImageUrl
                            && !this.leftNavReact
                            && !this.appBarEnabled
                            && !(route && route.app)
                            && route.route !== OfficeRoute.AppGallery;
                        this.backgroundStyle['background-image'] = this.showBackgroundImage ? `url(${this.contextService.sharedContext.fluentHeroImageUrl})` : '';
                });

                // Add fluent provider class to root of app to provide theme css variables
                if (this.fluentThemeRefreshEnabled) {
                    this.fluentProviderClassSubscription = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                        filter(event => event.name === AngularEvents.fluentClassNameChanged && event.args?.length === 1))
                        .subscribe(event => {
                            this.themeRootCssClass = ThemeRootCssClass + ' ' + event.args[0];
                        });
                }

                this.onWacContentPage = this.officeRouteService.getRouteChangeStartObservable().pipe(map(route => route.currentRoute.route === OfficeRoute.Content));

                const searchConfigService = this.injector.get(SearchConfigService);
                this.searchEnabled = this.capabilitiesService.capabilities.showSearch && !!searchConfigService.config;
                this.nativeSearchEnabled = this.capabilitiesService.capabilities.nativeSearchInHeaderEnabled;

                this.routeChangeSubscription = this.officeRouteService.getRouteChangeStartObservable().subscribe(routes => {
                    const newRoute = routes.currentRoute.route;
                    this.onAppGallery = newRoute === OfficeRoute.AppGallery;
                    this.onMyContentPage = newRoute === OfficeRoute.MyContent;
                    this.onFeedPage = newRoute === OfficeRoute.Feed;
                    this.showFASTBreadcrumb = newRoute === OfficeRoute.SearchSiteScoped;
                    this.showWorkspaceNav = this.onAppGallery && !this.appsModuleEnabled;

                    if (this.showWorkspaceNav) {
                        this.officeHomeClass.push('app-gallery-workspace-nav-offset');
                    } else {
                        this.removeOfficeHomeClass('app-gallery-workspace-nav-offset');
                    }

                    if (newRoute === OfficeRoute.Content && routes.currentRoute.app && routes.currentRoute.app === FileType.Fluid) {
                        this.showAnnouncement = false;
                    } else {
                        this.showAnnouncement = this.capabilitiesService.capabilities.announcementEnabled;
                    }
                });

                // Set FirstVisit time stamp
                const firstVisitTimestamp = this.localStorageService.get(this.firstVisitTimestampKey);
                if (!firstVisitTimestamp) {
                    this.localStorageService.set(this.firstVisitTimestampKey, new Date().toISOString());
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FirstSiteVisit);
                }

                const userContextService = this.injector.get(UserContextService);

                if (this.capabilitiesService.capabilities.firstRunReactEnabled) {
                    const firstRunStateServicePromise = import('@office-start/first-run-state-service');

                    firstRunStateServicePromise.then(mod => {
                        mod.fetchFirstRunState().then(firstRunState => {
                            if (!firstRunState.CompletedFirstRun) {
                                this.firstRunEnabled = true;

                                // TODO: implement tryPinToTaskbar() after user completes first run
                            } else {
                                    // Try ask user to pin this app to taskbar
                                this.tryPinToTaskbar();
                            }
                        });
                    });
                } else {
                    const firstRunConfigService = this.injector.get(FirstRunConfigService);
                    firstRunConfigService.getConfig().then(firstRunConfig => {
                        //TODO: Enable first run for mock mode in PWA on server side
                        this.firstRunEnabled = !userContextService.user.hasNoLicense && !!firstRunConfig && !!firstRunConfig.model && !this.contextService.sharedContext.inMockMode;
                        this.firstRunVersion = this.firstRunCalloutsConfigService.config.version;

                        // If this is a consumer user, we check for completion of first run on client side
                        const consumerFirstRunPromise = this.isConsumer ?
                            this.firstRunDataService.fetchFirstRunData(this.firstRunVersion,
                                true/*skipRoaming*/,
                                FirstRunDataService.firstRunDataKey)
                                .then(firstRunState => {
                                    if (firstRunState.CompletedFirstRun) {
                                        this.firstRunEnabled = false;
                                    }
                                }) : Promise.resolve();

                        consumerFirstRunPromise.then(() => {
                            if (this.firstRunEnabled) {
                                this.shellHeader = this.documentService.nativeDocument.getElementById('ReactShellHeader');
                                this.officeHomeClass = [];
                                this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                                    filter(event => event.name === AngularEvents.firstRunModalOpen),
                                    take(1))
                                    .toPromise()
                                    .then(() => {
                                        this.officeHomeClass.push('blur-background');
                                        this.shellHeader.classList.add('blur-background');
                                    });
                                this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                                    filter(event => event.name === AngularEvents.firstRunModalClosed),
                                    take(1))
                                    .toPromise()
                                    .then(() => {
                                        const index = this.officeHomeClass.indexOf('blur-background');
                                        if (index >= 0) {
                                            this.officeHomeClass.splice(index, 1);
                                        }
                                        this.shellHeader.classList.remove('blur-background');
                                        // Try pin to taskbar after first run experience
                                        this.tryPinToTaskbar();
                                    });
                            } else {
                                // Try ask user to pin this app to taskbar
                                this.tryPinToTaskbar();
                            }
                        });
                    });
                }

                this.interactiveAppDisplayed.then(() => this.firstRunCalloutsService.initializeFirstRun());

                // Check if Feature callout module should be downloaded
                this.featureCalloutSubscription = this.featureCalloutService.callouts.subscribe(callouts => {
                    this.loadFeatureCalloutModule = callouts.length > 0;
                });

                const teachingUiReactNewUserEnabled = this.capabilitiesService.capabilities.teachingUiReactNewUserEnabled;

                // Default to not loading react teaching ui
                this.loadTeachingUiReactModule = teachingUiReactNewUserEnabled;
                this.teachingUiReactModuleEnabled = teachingUiReactNewUserEnabled;

                // When teachingUiReactNewUserEnabled is disabled, default to loading existing Angular feature callouts.
                // If there aren't any, then load the React version for existing users
                if (!teachingUiReactNewUserEnabled) {
                    // Check if Feature callout module should be downloaded.
                    this.featureCalloutSubscription = this.featureCalloutService.callouts.subscribe(callouts => {
                        this.loadFeatureCalloutModule = callouts.length > 0;
                        // Don't load the module if there are angular callouts showing
                        this.loadTeachingUiReactModule =
                            !this.loadFeatureCalloutModule
                            && this.teachingUiReactModuleEnabled;
                    });

                    if (this.capabilitiesService.capabilities.teachingUiReactEnabled) {
                        this.injector.get(FirstRunCalloutsService).shouldShowCallouts().then(willShowAngularCallouts => {
                            // Only load the react module when we will not be showing any existing callouts
                            this.loadTeachingUiReactModule = !willShowAngularCallouts;
                            // Make sure we don't load react callouts after angular ones
                            this.teachingUiReactModuleEnabled = !willShowAngularCallouts;
                        });
                    }
                }

                this.dragAndDropUtility = new DragAndDrop(
                    this.documentService,
                    this.injector.get(UploadConfigService),
                    this.injector.get(UploadButtonService),
                    this.injector.get(AccessTokenService),
                    this.isConsumer);

                const scheduler = this.injector.get(SchedulerService);

                // Load all shell resources >= Shell Plus when idle.
                scheduler.scheduleWork(() => {
                    shellShimService.getShellShim().then(shellShim => {
                        if (shellShim.Header) {
                            shellShim.Header.OnShyHeaderModeChanged((hidden: boolean) => {
                                if (hidden) {
                                    this.officeHomeClass.push('shell--hidden');
                                } else {
                                    this.removeOfficeHomeClass('shell--hidden');
                                }

                                this.changeDetectorRef.detectChanges();
                            });
                        }

                        if (shellShim.GetResourceLoader) {
                            shellShim.GetResourceLoader().loadAll();
                        }
                    });
                }, SchedulePriority.VeryLow);

                if (this.isConsumer && !defaultHeaderLayoutEnabled) {
                    scheduler.scheduleWork(() => import('../shared/utilities/upsell-button.utility').then(chunk => {
                        this.upsellButtonUtility = new chunk.UpsellButton(this.consumerInstallConfigService, this.contextService, this.eventService, this.injector, this.officeRouteService, this.capabilitiesService, this.injector.get(WorkloadPublicDataConfigService));
                    }), SchedulePriority.VeryLow);
                    scheduler.scheduleWork(() => import('../shared/utilities/meet-now-button.utility').then(chunk => {
                        this.meetNowButtonUtility = new chunk.MeetNowButton(this.contextService, this.eventService, this.injector);
                    }), SchedulePriority.VeryLow);
                }

                if (this.isConsumer && defaultHeaderLayoutEnabled) {
                    scheduler.scheduleWork(() => {
                        import('../shared/services/shell-bootstrap.service').then(chunk => {
                            this.shellBootstrapService = new chunk.ShellBootstrapService(this.capabilitiesService, startupConfigService, consumerInstallConfigService, consumerPublicDataService, this.contextService, this.documentService, this.eventService, this.featureCalloutService, installConfigService, this.instrumentationService, locService, this.localStorageService, this.officeRouteService, reactShellConfigService, shellShimService, upsellDataService, urlLauncherService);
                            // calling shellbootstrapservice for Benefits, GoPremium, and MeetNow
                            this.shellBootstrapService.triggerMeetNow();
                            this.shellBootstrapService.triggerGoPremium();
                            this.shellBootstrapService.triggerBenefits();
                        });
                    }, SchedulePriority.VeryLow);
                }

                if (this.capabilitiesService.capabilities.visualSettingEnabled) {
                    scheduler.scheduleWork(() => {
                        import('../shared/services/shell-bootstrap.service').then(chunk => {
                            if (!this.shellBootstrapService) {
                                this.shellBootstrapService = new chunk.ShellBootstrapService(this.capabilitiesService, startupConfigService, consumerInstallConfigService, consumerPublicDataService, this.contextService, this.documentService, this.eventService, this.featureCalloutService, installConfigService, this.instrumentationService, locService, this.localStorageService, this.officeRouteService, reactShellConfigService, shellShimService, upsellDataService, urlLauncherService);
                            }
                            this.shellBootstrapService.registerSettingsFlexPane();
                        });
                    }, SchedulePriority.VeryLow);
                }

                // Enable feedback launching from shell
                const feedbackConfigService: FeedbackConfigService = this.injector.get(FeedbackConfigService);
                if (feedbackConfigService.config) {
                    const feedbackService: FeedbackService = this.injector.get(FeedbackService);

                    // Check tenant admin policy if flight is enabled
                    if (this.capabilitiesService.capabilities.adminPolicyEnabled) {
                        const adminPolicyService: AdminPolicyService = this.injector.get(AdminPolicyService);
                        adminPolicyService.getAdminPolicy().then(adminpolicy => {
                            // Only load OCV SendFeedback is enabled.
                            const adminEnabled = adminpolicy ? adminpolicy.SendFeedback.State : false;
                            if (adminEnabled) {
                                this.setupOcvFeedback({
                                    consumerInstallConfigService,
                                    consumerPublicDataService,
                                    feedbackService,
                                    installConfigService,
                                    locService,
                                    reactShellConfigService,
                                    scheduler,
                                    shellShimService,
                                    startupConfigService,
                                    upsellDataService,
                                    urlLauncherService
                                });
                            }
                        });

                    } else {
                        this.setupOcvFeedback({
                            consumerInstallConfigService,
                            consumerPublicDataService,
                            feedbackService,
                            installConfigService,
                            locService,
                            reactShellConfigService,
                            scheduler,
                            shellShimService,
                            startupConfigService,
                            upsellDataService,
                            urlLauncherService
                        });
                    }
                }

                this.viewInitializationCompleted = true;

                // Initial Navigation is deferred until view is loaded and <router-outlet> is available
                if (!this.router.navigated) {
                    this.router.initialNavigation();
                }

                // TODO: Replace with Edgeworth once MRU is replaced
                const eventPromises = (events: Array<AngularEvents>) =>
                    events.map(event => this.eventService.whenEventFired(event, ObservableScope.PastAndFuture));

                const promises = eventPromises([AngularEvents.mruReady, AngularEvents.recommendedReady, AngularEvents.appBarReady]);

                Promise.race(promises)
                    .then(() => {
                        console.log('logging lifeCycle responsive event');
                        this.windowService.nativeWindow.logPwaLifecycleEvent(
                            this.windowService.nativeWindow.pwaResuming
                                ? MyOffice.RuntimeComponents.LifeCycleEventType.ResumeResponsive
                                : MyOffice.RuntimeComponents.LifeCycleEventType.LaunchResponsive
                        );
                    })
                    .catch(); // if an exception is thrown we probably shouldn't log that as a valid perf data point

                // handle pending on activate event
                try {
                    const savedArgs = this.localStorageService.getGlobal(PwaRootComponent.PwaOnActivatePendingEventArgsKey);
                    if (savedArgs && savedArgs.by && savedArgs.argument) {
                        this.handlePendingEvent(savedArgs);
                    }
                } catch (error) {
                    const errorParams: InstrumentationParams = {};
                    errorParams[InstrumentationParam.Error] = (error || '').toString();
                    errorParams[InstrumentationParam.Action] = 'HandlePendingEventFromLocalStorage';
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaOnActivated, errorParams, [], DataSecurityLevel.Enhanced);
                    logError({ eventName: KnownErrorInstrumentationEvents.PwaOnActivated, message: (error || '').toString(), debugInfo: 'HandlePendingEventFromLocalStorage' }, otelDataSecurityLevel.Enhanced);
                }
                this.localStorageService.removeGlobal(PwaRootComponent.PwaOnActivatePendingEventArgsKey);

                if (this.capabilitiesService.capabilities.showFloodgate && this.capabilitiesService.capabilities.showPwaFeelingsSurvey) {
                    const date = new Date();
                    const dayOfMonth = date.getDate();
                    if (date.getMonth() === 8 && ((dayOfMonth >= 3 && dayOfMonth < 14) || (dayOfMonth >= 24 && dayOfMonth < 28))) {
                        this.floodgateService.recordFloodgate(['IsCorrectDay']);
                    }
                }

                this.visualConfigSubscription = this.visualService.visualConfigSubscription.subscribe(visualEvent => {
                    this.visualBackgroundEnabled = visualEvent.visualBackgroundEnabled;
                    this.visualOfficeWrapperStyle = visualEvent.visualOfficeWrapperStyle;
                });
            });
        const navigator = this.windowService.nativeWindow.navigator;
        if (navigator && typeof (navigator.onLine) === 'boolean' && !navigator.onLine) {
            this.sendOfflineNotification();
        }
    }

    public ngOnDestroy() {
        if (this.backgroundImageSubscription) {
            this.backgroundImageSubscription.unsubscribe();
        }

        if (this.featureCalloutSubscription) {
            this.featureCalloutSubscription.unsubscribe();
        }

        if (this.upsellButtonUtility) {
            this.upsellButtonUtility.unsubscribe();
        }

        if (this.meetNowButtonUtility) {
            this.meetNowButtonUtility.unsubscribe();
        }

        if (this.routeChangeSubscription) {
            this.routeChangeSubscription.unsubscribe();
        }

        this.fluentProviderClassSubscription?.unsubscribe();

        if (this.shellBootstrapService) {
            this.shellBootstrapService.unsubscribe();
        }

        if (this.visualConfigSubscription) {
            this.visualConfigSubscription.unsubscribe();
        }
    }

    @HostListener('window:offline')
    public sendOfflineNotification() {
        const iconClass = ClassNames.FluentIconCssClass;
        const resources = this.bannerConfigService.config.resources;
        this.eventService.broadcastEvent({
            name: AngularEvents.newAnnouncement,
            args: [new AnnouncementModel(AnnouncementType.OfflineMode, resources.OfflineModeMessage, null, `${iconClass} ${ClassNames.getCssClass(IconFontType.CircleInfo)}`)]
        });
    }

    @HostListener('window:online')
    public sendOnlineNotification() {
        this.eventService.broadcastEvent({
            name: AngularEvents.closeAnnouncement,
            args: [AnnouncementType.OfflineMode]
        });
    }

    @HostListener('window:focus')
    public onWindowFocus() {
        this.eventService.broadcastEvent({
            name: AngularEvents.windowFocused,
            args: []
        });
    }

    @HostListener('window:resize')
    public onWindowResize() {
        this.eventService.broadcastEvent({
            name: AngularEvents.windowResized,
            args: []
        });
    }

    @HostListener('window:activated', ['$event'])
    public onActivate(event) {
        if (event && event.detail && event.detail.by && event.detail.argument) {
            this.handlePendingEvent(event.detail);
        }

        this.localStorageService.removeGlobal(PwaRootComponent.PwaOnActivatePendingEventArgsKey);
    }

    public onResize(contentRect: DOMRectReadOnly) {
        const { width, height } = contentRect;

        if (this.contentWidth !== width) {
            this.contentWidth = width;
            this.eventService.broadcastEvent({
                name: AngularEvents.contentWidthChanged,
                args: [width]
            });
        }

        if (this.contentHeight !== height) {
            this.contentHeight = height;
            this.eventService.broadcastEvent({
                name: AngularEvents.contentHeightChanged,
                args: [height]
            });
        }
    }

    public dragoverHandler(event: DragEvent) {
        event.preventDefault();
        this.dragAndDropUtility.onDragOver(event);
    }

    public dropHandler(event: DragEvent) {
        event.preventDefault();
        this.dragAndDropUtility.onDrop(event);
    }

    private removeOfficeHomeClass(className: string) {
        this.officeHomeClass = this.officeHomeClass ? this.officeHomeClass.filter(el => el !== className) : [];
    }

    private tryPinToTaskbar() {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = 'Init';
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PinToTaskbar, params, [], DataSecurityLevel.Enhanced);

        const pinDismissedCookieName = 'pinToTaskbarDismissed';
        if (this.cookieService.get(pinDismissedCookieName)) {
            return;
        }

        this.pinToTaskbarService.isEligibleToPin().then(eligible => {
            if (eligible) {
                params[InstrumentationParam.State] = 'Eligible';
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PinToTaskbar, params, [], DataSecurityLevel.Enhanced);

                this.pinToTaskbarService.pinToTaskbar().then(success => {
                    params[InstrumentationParam.State] = success ? 'Success' : 'Failed';
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PinToTaskbar, params, [], DataSecurityLevel.Enhanced);
                    const expireDate = new Date();
                    expireDate.setFullYear(expireDate.getFullYear() + 50);
                    this.cookieService.set(pinDismissedCookieName, '1', expireDate, '/' /* path */, undefined, true /* secure */);
                });
            }
        });
    }

    private handlePendingEvent(args) {
        switch (args.by) {
            case 'JumpList':
                this.handleJumpListEvent(args.argument, args.previousExecutionState);
                break;
            case 'WordPad':
            case 'CovidLockscreen':
            case 'CovidStartMenu':
            case 'CovidAMC':
            case 'CovidActionCenter':
            case 'HVAStartMenu':
            case 'CGEAwareness':
                this.handleNavigationEvent(args.argument);
                break;
            case 'M365Apps':
                this.handleUrlNavigationEvent(args.argument);
                break;
        }
    }

    private async handleUrlNavigationEvent(url) {
        const m365AppsUrl = `${window.location.origin}/${RoutePaths.M365Apps}${url}`;
        if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
            await import('@office-start/launcher-service').then(
                launchSvc => {
                    launchSvc.launchUrl(m365AppsUrl, {action: 'OpenInApp', linkTarget: '_self'});
                }
            );
        } else {
            await this.urlLauncherService.openUrl(m365AppsUrl, LaunchAction.OpenInApp, LinkTarget.SameFrame);
        }
    }

    private handleNavigationEvent(args) {
        if (args === 'Word') {
            this.officeRouteService.navigateTo({ route: OfficeRoute.Start, app: FileType.Word });
        } else if (args === 'Excel') {
            this.officeRouteService.navigateTo({ route: OfficeRoute.Start, app: FileType.Excel });
        } else if (args === 'PowerPoint') {
            this.officeRouteService.navigateTo({ route: OfficeRoute.Start, app: FileType.PowerPoint });
        }
    }

    private async handleJumpListEvent(args, previousExecutionState: number) {
        if (this.isPwaJumpListEnabled) {
            let exitApp;
            try {
                const argument = JSON.parse(args);
                if (typeof argument.createFileType !== 'undefined') {
                    exitApp = await this.createNewFile(argument.createFileType);
                } else if (typeof argument.id !== 'undefined') {
                    const jumpListItem = this.jumpListService.getJumpListItemById(argument.id);
                    exitApp = await this.openJumpListFiles(jumpListItem);
                }
            } catch (error) {
                const errorParams = {};
                errorParams[InstrumentationParam.Error] = (error || '').toString();
                errorParams[InstrumentationParam.Action] = 'ActivateByJumpListEventReceived';
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, errorParams, [], DataSecurityLevel.Enhanced);
                logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaJumpList, params: errorParams }, otelDataSecurityLevel.Enhanced);

                exitApp = false;
            }
            this.exitAppOrMakeAppVisible(exitApp, previousExecutionState);
        }
    }

    private async openJumpListFiles(pwaJumpListItem: JumpListItem): Promise<boolean> {
        if (!pwaJumpListItem) {
            return false;
        }
        return new Promise(async resolve => {
            const params = {};
            params[InstrumentationParam.State] = 'Handled';
            params[InstrumentationParam.ItemCategory] = pwaJumpListItem.group;
            params[InstrumentationParam.Action] = 'OpenFile';
            const referral = MruFileItem.referralFromFileType(pwaJumpListItem.fileType);
            const elementId = 'jump-list-file-item';
            const area = 'jump-list';

            const logJumpListEvent = (status: boolean, platform: KnownReferralPlatform, uiHost: boolean, error?: string ) => {
                if (!status) {
                    params[InstrumentationParam.Error] = error || (uiHost ? 'JumpListFileOpenUIHostError' : 'JumpListFileOpenError');
                    params[InstrumentationParam.Referral] = referral;
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, params, [], DataSecurityLevel.Enhanced);
                    logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaJumpList, params }, otelDataSecurityLevel.Enhanced);
                } else {
                    this.instrumentationService.logClickEvent(KnownInstrumentationEvents.PwaJumpListItemClick, elementId, area, referral, platform, params, [], DataSecurityLevel.Enhanced);
                    logUserAction({ id: elementId, area: 'JumpList', eventName: KnownInstrumentationEvents.PwaJumpListItemClick, result: 'Refer', target: getReferral(referral)}, otelDataSecurityLevel.Enhanced);
                }
            };

            try {
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchApp({ action: 'OpenMruItem', appName: pwaJumpListItem.application, url: pwaJumpListItem.canonicalUrl, useUrl: true })
                            .then(result => {
                                if (result.success) {
                                    // This resolves to true where app exits
                                    logJumpListEvent(true, KnownReferralPlatform.Native, false);
                                    return resolve(true);
                                } else if (pwaJumpListItem.isLocal) {
                                    // Open modal and ask for upload. This resolves to false where app window is made visible
                                    this.uploadLocalFileService.showModal(pwaJumpListItem.url, pwaJumpListItem.fileName, UploadActionType.Open);
                                    params[InstrumentationParam.Action] = 'OpenFileNeedUpload';
                                    logJumpListEvent(true, KnownReferralPlatform.Web, false);
                                    return resolve(false);
                                }
                            });
                        }
                    );
                } else {
                    // Launch native Office app to open local MRU files
                    const success = await this.localAppLauncherService.launchOfficeApp(pwaJumpListItem.application, pwaJumpListItem.canonicalUrl, null);
                    if (success) {
                        // This resolves to true where app exits
                        logJumpListEvent(true, KnownReferralPlatform.Native, false);
                        return resolve(true);
                    } else if (pwaJumpListItem.isLocal) {
                        // Open modal and ask for upload. This resolves to false where app window is made visible
                        this.uploadLocalFileService.showModal(pwaJumpListItem.url, pwaJumpListItem.fileName, UploadActionType.Open);
                        params[InstrumentationParam.Action] = 'OpenFileNeedUpload';
                        logJumpListEvent(true, KnownReferralPlatform.Web, false);
                        return resolve(false);
                    }
                }
            } catch (error) {
                // On error try to launch Web app
            }

            const wacHostFile = this.capabilitiesService.wacHostEnabledForFileType(pwaJumpListItem.fileType) && !pwaJumpListItem.isLocal;
            const hasTokenForWacHost = ((!pwaJumpListItem.isOnPremOrVanity || this.isConsumer) && this.accessTokenService.hasOneDriveToken())
                || this.contextService.sharedContext.inMockMode;

            try {
                // Added ContentHandler
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {

                    const eventName = KnownInstrumentationEvents.PwaJumpList;

                    const document: DocumentReact = {
                        app: pwaJumpListItem.application,
                        attribution: pwaJumpListItem.fileTypeName,
                        canonicalUrl: pwaJumpListItem.canonicalUrl,
                        docId: pwaJumpListItem.docId,
                        driveId: pwaJumpListItem.driveId,
                        extension: pwaJumpListItem.fileTypeName,
                        fileName: pwaJumpListItem.fileName,
                        id: pwaJumpListItem.jumpListItemId,
                        isLocalFile: pwaJumpListItem.isLocal,
                        isOnPremOrVanity: pwaJumpListItem.isOnPremOrVanity,
                        title: pwaJumpListItem.fileName,
                        url: pwaJumpListItem.url
                    };

                    const fileClickData = getFileClickData(
                        document,
                        eventName,
                        0,
                        'Click',
                        'PwaDialog'
                    );

                    await import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchContent(
                                {
                                    action: 'OpenInApp',
                                    document,
                                    linkTarget: (event && event['ctrlKey']) ? '_blank' : '_self',
                                    wdOriginType: WdOriginTypeInst.New
                                },
                                {
                                    area: 'PwaDialog',
                                    eventName,
                                    elementId: pwaJumpListItem.jumpListItemId,
                                    referral: getReferralFromFileType(FileType[pwaJumpListItem.fileType] as FileTypeReact),
                                    clickParams: fileClickData
                                }
                            ).then(() => {
                                if (wacHostFile && hasTokenForWacHost) {
                                    logJumpListEvent(true, KnownReferralPlatform.Web, true);
                                    return resolve(false);
                                } else {
                                    logJumpListEvent(true, KnownReferralPlatform.Web, false);
                                    return resolve(true);
                                }
                            });
                        }
                    );
                } else {
                    // Remove when ContentHandler is enabled for PWA
                    if (wacHostFile && hasTokenForWacHost) {
                        // UIHost launch
                        const documentInfo: DocumentInfo = {
                            docId: pwaJumpListItem.docId,
                            docUrl: pwaJumpListItem.canonicalUrl,
                            driveId: pwaJumpListItem.driveId,
                            fileType: pwaJumpListItem.fileType,
                            fileName: pwaJumpListItem.fileName,
                            wdOrigin: this.contextService.getWdOrigin(WdOriginType.JumpList)
                        };
                        await this.wacContentService.bootstrapAndRoute(
                            documentInfo,
                            undefined,
                            Date.now()
                        );
                        logJumpListEvent(true, KnownReferralPlatform.Web, true);
                        return resolve(false);
                    }
                    // Browser app launch
                    if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                        await import('@office-start/launcher-service').then(
                            launchSvc => {
                                launchSvc.launchUrl(pwaJumpListItem.url, {action: 'OpenJumpListItem'});
                            }
                        );
                    } else {
                        await this.urlLauncherService.openUrl(pwaJumpListItem.url, LaunchAction.OpenJumpListItem);
                    }

                    logJumpListEvent(true, KnownReferralPlatform.Web, false);
                    return resolve(true);
                }
            } catch (error) {
                logJumpListEvent(false, KnownReferralPlatform.Web, wacHostFile && hasTokenForWacHost, (error).toString());
                return resolve(false);
            }
        });
    }

    private async setupOcvFeedback(feedbackOptions: {
        consumerInstallConfigService: ConsumerInstallConfigService,
        consumerPublicDataService: WorkloadPublicDataConfigService,
        feedbackService: FeedbackService,
        installConfigService: InstallConfigService,
        locService: LocService,
        reactShellConfigService: ReactShellConfigService
        scheduler: SchedulerService,
        shellShimService: ShellShimService,
        startupConfigService: ClientStartupConfigService,
        upsellDataService: UpsellDataService,
        urlLauncherService: UrlLauncherService
    }): Promise<void> {
        const { reactShellConfigService, feedbackService } = feedbackOptions;
        if (this.capabilitiesService.capabilities.visualSettingEnabled) {
            const { consumerPublicDataService, consumerInstallConfigService, installConfigService, locService, scheduler, shellShimService, startupConfigService, upsellDataService, urlLauncherService } = feedbackOptions;
            scheduler.scheduleWork(() => {
                import('../shared/services/shell-bootstrap.service').then(chunk => {
                    if (!this.shellBootstrapService) {
                        this.shellBootstrapService = new chunk.ShellBootstrapService(this.capabilitiesService, startupConfigService, consumerInstallConfigService, consumerPublicDataService, this.contextService, this.documentService, this.eventService, this.featureCalloutService, installConfigService, this.instrumentationService, locService, this.localStorageService, this.officeRouteService, reactShellConfigService, shellShimService, upsellDataService, urlLauncherService);
                    }
                    this.shellBootstrapService.registerSettingsFlexPaneFeedbackLink(
                        reactShellConfigService.reactShellConfig.feedbackLinkText, () => {
                            this.eventService.broadcastEvent({name: AngularEvents.shellOpenFeedback, args: []});
                    });
                });
            }, SchedulePriority.VeryLow);
        } else {
            HomePage.ShellUtil.ShowOCVFeedbackLink(reactShellConfigService.reactShellConfig);
        }

        this.eventService.events.withScope(ObservableScope.OnlyFuture)
        .pipe(filter(event => event.name === AngularEvents.shellOpenFeedback))
        .subscribe(() => feedbackService.loadOCVFeedback());
    }

    private async createNewFile(fileType: string): Promise<boolean> {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = 'Handled';
        params[InstrumentationParam.Action] = 'CreateNewFile';
        const elementId = 'jump-list-task-item';
        const area = 'jump-list';
        let referral = MruFileItem.referralFromFileType(FileType[fileType]);

        // TODO: Remove app launch once native Excel supports 0-byte create flow (also see line 321 of create-document.service)
        if (this.capabilitiesService.capabilities.pwaEnabled && FileType[fileType] === FileType.Excel) {
            try {
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    const launchSvc = await import('@office-start/launcher-service');
                    const nativeInstalled = await launchSvc.shouldOpenInOfficeApp(fileType);

                    if (nativeInstalled) {
                        const success = await launchSvc.launchApp({ action: 'OpenMruItem', appName: FileType[FileType.Excel], url: '' });
                        if (success) {
                            this.instrumentationService.logClickEvent(KnownInstrumentationEvents.PwaJumpListItemClick, elementId, area, referral, KnownReferralPlatform.Native, params, [], DataSecurityLevel.Enhanced);
                            logUserAction({ id: elementId, area: 'JumpList', eventName: KnownInstrumentationEvents.PwaJumpListItemClick, result: 'Refer', target: getReferral(referral)}, otelDataSecurityLevel.Enhanced);
                            return true;
                        }
                    } else {
                        //Continue to call create document for web app case
                    }
                } else {
                    // Launch with old service to open local MRU files
                    const success = await this.localAppLauncherService.launchOfficeApp(FileType[FileType.Excel], '', null, true);
                    if (success) {
                        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.PwaJumpListItemClick, elementId, area, referral, KnownReferralPlatform.Native, params, [], DataSecurityLevel.Enhanced);
                        logUserAction({ id: elementId, area: 'JumpList', eventName: KnownInstrumentationEvents.PwaJumpListItemClick, result: 'Refer', target: getReferral(referral)}, otelDataSecurityLevel.Enhanced);
                        return true;
                    }
                }
            } catch (error) {
                // On error call create document
            }
        }

        return new Promise(resolve => {
            this.createDocumentService.createItem(FileType[fileType], null, null, (result: CreateDocumentResult) => {
                referral = (result.type === CreateDocumentResultType.Error || result.type === CreateDocumentResultType.CreatePending) ? '' : referral;
                this.instrumentationService.logClickEvent(KnownInstrumentationEvents.PwaJumpListItemClick, elementId, area, referral, result.platform, params, [], DataSecurityLevel.Enhanced);
                logUserAction({ id: elementId, area: 'JumpList', eventName: KnownInstrumentationEvents.PwaJumpListItemClick, result: referral ? 'Refer' : 'Navigate', target: referral ? getReferral(referral) : 'Other'}, otelDataSecurityLevel.Enhanced);
                resolve(!(result.type === CreateDocumentResultType.Error || result.type === CreateDocumentResultType.CreatePending || (result.type === CreateDocumentResultType.GoToBlankDoc && result.platform === KnownReferralPlatform.Web)));
            });
        });
    }

    private exitAppOrMakeAppVisible(exitApp: boolean, previousExecutionState: number) {
        // previousExecutionState: 0 i.e.NotRunning
        if (!(previousExecutionState === 0 && typeof (Windows.ApplicationModel.Core) !== 'undefined' && typeof (Windows.ApplicationModel.Core.CoreApplication) !== 'undefined' && typeof (Windows.ApplicationModel.Core.CoreApplication.exit) !== 'undefined'
            && typeof (Windows.UI.WindowManagement) !== 'undefined' && typeof (Windows.UI.WindowManagement.AppWindow) !== 'undefined')) {
            return;
        }
        try {
            if (exitApp) {
                // If JumpList document was opened successfully, close the app
                Windows.ApplicationModel.Core.CoreApplication.exit();
            } else {
                // If JumpList document was not opened successfully, make the app visible
                const appWindow = new Windows.UI.WindowManagement.AppWindow();
                appWindow.tryShowAsync().then(result => {
                    if (!result) {
                        Windows.ApplicationModel.Core.CoreApplication.exit();
                    }
                });
            }
            return;
        } catch (error) {
            const params = {};
            params[InstrumentationParam.Error] = (error || 'ExitAppOrMakeAppVisible error').toString();
            params[InstrumentationParam.Action] = exitApp ? 'ExitAppOnJumpListHandled' : 'MakeAppVisibleOnJumpListHandled';
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaJumpList, params, [], DataSecurityLevel.Enhanced);
        }
    }
}
